import classnames from "classnames";
import { PanelLeftClose, SlidersHorizontal } from "lucide-react";
import { useState } from "react";

import { useTranslation } from "@/lib/i18n";

import {
  riskPictureFilterPhenomena,
  riskPicturePhenomenonAvailable,
  type RiskPictureMapFilter,
} from "./risk-picture-map-filter";
import type { RiskPictureItem } from "./risk-picture-model";
import {
  riskPicturePhenomenonIconUrl,
  riskPicturePhenomenonName,
} from "./risk-picture-phenomena";
import styles from "./risk-picture.module.css";

interface RiskPictureMapFiltersProps {
  items: RiskPictureItem[];
  /** Seasonal catalog, plus payload phenomena outside it. */
  phenomena: readonly string[];
  filter: RiskPictureMapFilter;
  onChange: (filter: RiskPictureMapFilter) => void;
}

const PILL =
  "M16 5H8C4.13401 5 1 8.13401 1 12C1 15.866 4.13401 19 8 19H16C19.866 19 23 15.866 23 12C23 8.13401 19.866 5 16 5Z";
const KNOB_ON =
  "M16 15C17.6569 15 19 13.6569 19 12C19 10.3431 17.6569 9 16 9C14.3431 9 13 10.3431 13 12C13 13.6569 14.3431 15 16 15Z";
const KNOB_OFF =
  "M8 15C9.65685 15 11 13.6569 11 12C11 10.3431 9.65685 9 8 9C6.34315 9 5 10.3431 5 12C5 13.6569 6.34315 15 8 15Z";

const FilterSwitchIcon = ({ checked }: { checked: boolean }) => (
  // Cropped to the pill (y 5–19 plus stroke) so the empty rows of the 24×24 export don't add height.
  <svg
    width="32"
    height="21"
    viewBox="0 4 24 16"
    fill="none"
    aria-hidden="true"
    // var() isn't reliable in SVG presentation attributes; paths inherit via currentColor.
    style={{ color: "var(--navy)" }}
  >
    <path
      d={PILL}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
    <path
      d={checked ? KNOB_ON : KNOB_OFF}
      fill={checked ? "currentColor" : "none"}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
  </svg>
);

const toggleHidden = (hidden: ReadonlySet<string>, phenomenon: string): Set<string> => {
  const next = new Set(hidden);
  if (next.has(phenomenon)) next.delete(phenomenon);
  else next.add(phenomenon);
  return next;
};

/** Kind switches and phenomenon icons over the big map. Region layers are not part of this. */
export const RiskPictureMapFilters = ({
  items,
  phenomena,
  filter,
  onChange,
}: RiskPictureMapFiltersProps) => {
  const { t } = useTranslation();
  const buttons = riskPictureFilterPhenomena(phenomena);
  // Closed by default. Above 1100px the toggle is hidden and the rows stay visible.
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className={classnames(
          styles.filterOpenToggle,
          open && styles.filterOpenToggleHidden,
        )}
        aria-label={t("riskPicture.filters.openAria")}
        aria-hidden={open}
        tabIndex={open ? -1 : undefined}
        onClick={() => setOpen(true)}
      >
        <SlidersHorizontal size={20} />
      </button>
      <div className={classnames(styles.mapFilters, !open && styles.mapFiltersClosed)}>
        <div className={styles.filterToolbar}>
          <button
            type="button"
            className={styles.filterClose}
            aria-label={t("riskPicture.filters.closeAria")}
            onClick={() => setOpen(false)}
          >
            <PanelLeftClose size={20} />
          </button>
        </div>
        <div className={styles.filterRow}>
          <span>{t("riskPicture.filters.issued")}</span>
          <button
            type="button"
            role="switch"
            aria-checked={filter.showIssued}
            className={styles.filterSwitch}
            onClick={() => onChange({ ...filter, showIssued: !filter.showIssued })}
          >
            <FilterSwitchIcon checked={filter.showIssued} />
          </button>
        </div>
        <div className={styles.filterRow}>
          <span>{t("riskPicture.filters.chance")}</span>
          <button
            type="button"
            role="switch"
            aria-checked={filter.showChance}
            className={styles.filterSwitch}
            onClick={() => onChange({ ...filter, showChance: !filter.showChance })}
          >
            <FilterSwitchIcon checked={filter.showChance} />
          </button>
        </div>
        <div className={styles.filterPhenomena}>
          <span className={styles.filterPhenomenaLabel}>
            {t("riskPicture.filters.phenomena")}
          </span>
          <div className={styles.filterPhenomenaGrid}>
            {buttons.map((phenomenon) => {
              const available = riskPicturePhenomenonAvailable(items, phenomenon, filter);
              const selected = available && !filter.hiddenPhenomena.has(phenomenon);
              const name = riskPicturePhenomenonName(phenomenon, t);
              const label = available
                ? name
                : t("riskPicture.filters.phenomenonUnavailable", { name });
              return (
                <button
                  key={phenomenon}
                  type="button"
                  className={styles.filterPhenomenon}
                  aria-pressed={selected}
                  aria-label={label}
                  title={label}
                  disabled={!available}
                  onClick={() =>
                    onChange({
                      ...filter,
                      hiddenPhenomena: toggleHidden(filter.hiddenPhenomena, phenomenon),
                    })
                  }
                >
                  <span
                    className={styles.filterPhenomenonIcon}
                    style={{
                      maskImage: `url("${riskPicturePhenomenonIconUrl(phenomenon)}")`,
                      WebkitMaskImage: `url("${riskPicturePhenomenonIconUrl(phenomenon)}")`,
                    }}
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};
